import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Portal,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Ban, Info } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

import { getLotStatusLabel } from '@modules/_shared/components/LotCardInfo';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { Tag } from '@modules/_shared/components/Tag';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  markAdvertisersBlockedInCache,
  markAdvertisersUnblockedInCache,
} from '@modules/_shared/utils/fsboBlockCache';
import {
  BLOCK_FSBO_USERS,
  GET_FSBO_BLOCK_BATCH,
  GET_FSBO_SELLER_CLUSTER,
  UNBLOCK_FSBO_USERS,
} from '@modules/listings/lotsQueries';
import { type Translate, useLocale } from '@shared/hooks/locale';

import { useFsboBlockDialog } from './FsboBlockDialogContext';
import {
  type ClusterKind,
  type LinkedAccount,
  STRONG_KINDS,
  batchToJoin,
  defaultBlockSelection,
  isStrongLink,
  pivotLinkedAccounts,
} from './fsboLinkedAccounts';

// The FSBO advertiser's standing on a listing page (#24256):
//   - blocked: a red banner with when / why / with whom, and an Unblock
//     button that can lift the whole batch;
//   - otherwise, the OTHER accounts of the last 90 days linked to this
//     submission (same browser, name, address, internet connection), one
//     row each with what links them and their listings' status, and ONE
//     button that blocks the advertiser together with the ticked accounts.
//     Strong links (browser, name, address) are ticked by default; a shared
//     IP range alone is not. Blocking is a shadow ban: the sellers see
//     nothing, their listings just never go live.
// Tenant admins only; agents see nothing of it.
export type FsboAdvertiser = {
  id: string;
  fsbo_blocked_at?: string | null;
  fsbo_blocked_reason?: string | null;
  fsbo_block_batch_id?: string | null;
};

// Mirrors MAX_TARGETS in functions/src/hasura-actions/routes/block-fsbo-users:
// the action refuses a longer user_ids list, and a large operator cluster
// (up to 50 listings per key, three strong keys) can pre-tick more than
// that. The dialog says so instead of letting the request 400.
const MAX_BLOCK_TARGETS = 50;

interface FsboAdvertiserStatusProps {
  lotId: string;
  advertiser: FsboAdvertiser | null;
  readOnly?: boolean;
}

const kindLabel = (
  kind: ClusterKind,
  networkOwner: string | null,
  t: Translate,
): string => {
  switch (kind) {
    case 'browser_id':
      return t('Same browser');
    case 'display_name':
      return t('Same advertiser name');
    case 'address':
      return t('Same property address');
    case 'ip_network':
      // Who the range belongs to is the reviewer's tell: neighbours share a
      // Swisscom range, nobody's home connection comes out of a hosting or
      // VPN company. Only known for submissions that carried the ASN.
      return networkOwner != null
        ? t('Same IP range ({{owner}})', { owner: networkOwner })
        : t('Same IP range');
  }
};

const statusSummary = (statuses: string[], t: Translate): string => {
  const unique = [...new Set(statuses)].map(s =>
    getLotStatusLabel(s, t).toLowerCase(),
  );
  return statuses.length > 1
    ? `${t('{{count}} listings', { count: statuses.length })}: ${unique.join(
        ', ',
      )}`
    : unique.join(', ');
};

const LinkedAccountRow = ({
  account,
  checked,
  disabled,
  onToggle,
}: {
  account: LinkedAccount;
  checked: boolean;
  disabled: boolean;
  onToggle: (userId: string, checked: boolean) => void;
}) => {
  const { t } = useLocale();
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled || account.blocked}
        onChange={e => onToggle(account.userId, e.target.checked)}
        sx={{ p: 0.25, mt: 0.1 }}
        slotProps={{
          input: { 'aria-label': account.name ?? t('Unnamed advertiser') },
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" component="div">
          <Link
            component={RouterLink}
            to={`/listings/${account.latestLotId}`}
            sx={{ fontWeight: 600 }}
          >
            {account.name ?? t('Unnamed advertiser')}
          </Link>{' '}
          <Typography variant="caption" component="span" color="text.secondary">
            {statusSummary(account.statuses, t)}
          </Typography>
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{ flexWrap: 'wrap', mt: 0.25 }}
        >
          {account.blocked && <Tag status="error">{t('blocked')}</Tag>}
          {account.kinds.map(kind => (
            <Tag
              key={kind}
              status={STRONG_KINDS.has(kind) ? 'warning' : 'neutral'}
              textWrap="wrap"
            >
              {kindLabel(kind, account.networkOwner, t)}
            </Tag>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export const FsboAdvertiserStatus: React.FC<FsboAdvertiserStatusProps> = ({
  lotId,
  advertiser,
  readOnly = false,
}) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const dialog = useFsboBlockDialog();
  const [localOpen, setLocalOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);
  const [unblockBatch, setUnblockBatch] = useState(true);
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // Portal unpublishes the block action could not complete (it returns the
  // count): the accounts ARE blocked and the listings are off the site, but
  // one may still show on a portal until someone toggles it off by hand.
  const [portalFailures, setPortalFailures] = useState(0);
  // Listings the action could not put back in pending review at all: the
  // accounts ARE blocked but those listings are still live on the site
  // until someone sets their status by hand. An error, not a warning.
  const [lotFailures, setLotFailures] = useState(0);

  const isAdmin = me?.is_admin === true;
  const blocked = advertiser?.fsbo_blocked_at != null;
  const canAct = isAdmin && !readOnly && advertiser != null;

  const { data: clusterData } = useQuery(GET_FSBO_SELLER_CLUSTER, {
    variables: { lot_id: lotId },
    fetchPolicy: 'cache-and-network',
    skip: !isAdmin,
  });
  const { accounts, hiddenAccounts } = useMemo(
    () =>
      pivotLinkedAccounts(
        (clusterData?.fsbo_seller_cluster ?? []).map(row => ({
          kind: row.kind,
          value: row.value,
          other_accounts: row.other_accounts,
          network_owner: row.network_owner,
          accounts: row.accounts,
        })),
      ),
    [clusterData],
  );
  // Strong links are ticked whenever the cluster (re)loads; the reviewer's
  // unticks survive re-renders but not a refetch, which is what we want
  // after a block: the list has changed under them.
  useEffect(() => {
    setSelected(new Set(defaultBlockSelection(accounts)));
  }, [accounts]);

  // Blocked together with whom: the rest of the batch, for the banner and
  // for the "also unblock" offer.
  const { data: batchData } = useQuery(GET_FSBO_BLOCK_BATCH, {
    variables: {
      batch_id: advertiser?.fsbo_block_batch_id ?? '',
      user_id: advertiser?.id ?? '',
    },
    skip: !isAdmin || !blocked || advertiser?.fsbo_block_batch_id == null,
  });
  const batchMates = batchData?.users ?? [];

  const [blockUsers, blockState] = useMutation(BLOCK_FSBO_USERS, {
    update: (cache, { data }) => {
      const out = data?.block_fsbo_users;
      if (out == null) {
        return;
      }
      markAdvertisersBlockedInCache(cache, {
        userIds: out.blocked_user_ids,
        // Only mirror the status / portal take-down when the action actually
        // completed it: patching a lot the hold failed on would show
        // "Pending review" / "Offline" for a listing that is still live and
        // contradict the error the reviewer is being shown. Without the
        // patch the page shows the real state after a reload.
        lotId:
          out.failed_lots === 0 && out.failed_portal_listings === 0
            ? lotId
            : null,
        reason: reason.trim() === '' ? null : reason.trim(),
        batchId: out.batch_id,
      });
    },
    refetchQueries: [GET_FSBO_SELLER_CLUSTER],
    onCompleted: data => {
      setPortalFailures(data.block_fsbo_users?.failed_portal_listings ?? 0);
      setLotFailures(data.block_fsbo_users?.failed_lots ?? 0);
    },
  });
  const [unblockUsers, unblockState] = useMutation(UNBLOCK_FSBO_USERS, {
    update: (cache, { data }) => {
      const out = data?.unblock_fsbo_users;
      if (out != null) {
        markAdvertisersUnblockedInCache(cache, out.user_ids);
      }
    },
    refetchQueries: [GET_FSBO_SELLER_CLUSTER],
  });

  // Tell the reject dialog whether "block instead" is on offer here.
  const available = canAct && !blocked;
  const setAvailable = dialog?.setAvailable;
  useEffect(() => {
    setAvailable?.(available);
    return () => setAvailable?.(false);
  }, [available, setAvailable]);

  if (advertiser == null || !isAdmin) {
    return null;
  }

  const busy = blockState.loading || unblockState.loading;
  const blockOpen = dialog?.open ?? localOpen;
  const openBlock = dialog?.openDialog ?? (() => setLocalOpen(true));
  const closeBlock = dialog?.closeDialog ?? (() => setLocalOpen(false));

  const toggle = (userId: string, checked: boolean) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  const selectable = accounts.filter(a => !a.blocked);
  const selectedAccounts = selectable.filter(a => selected.has(a.userId));
  const strong = accounts.some(isStrongLink);
  // The advertiser plus the ticked accounts go in one call.
  const overCap = 1 + selectedAccounts.length > MAX_BLOCK_TARGETS;

  // Nothing to flag and nobody who could act: stay out of the way.
  if (!blocked && accounts.length === 0 && !canAct) {
    return null;
  }

  // Both submits swallow the mutation's rejection: the error is already on
  // blockState / unblockState for the snackbar below, and an uncaught
  // rejection from the click handler would only add Sentry noise on the one
  // path a reviewer will hit (a 403 on a staff target, an unknown batch).
  // The dialog stays open on failure.
  const submitBlock = async () => {
    try {
      await blockUsers({
        variables: {
          user_ids: [advertiser.id, ...selectedAccounts.map(a => a.userId)],
          lot_id: lotId,
          reason: reason.trim() === '' ? null : reason.trim(),
          batch_id: batchToJoin(accounts),
        },
      });
    } catch {
      return;
    }
    setReason('');
    closeBlock();
  };

  // The action takes at most MAX_BLOCK_TARGETS ids per call and a batch can
  // hold more than that (every automatic same-browser block joins it), so
  // the unblock goes out in chunks — the batch mates first and the
  // advertiser LAST. Until the advertiser's own row is cleared this banner
  // and the batch list stay on screen, so if a chunk fails part-way the
  // reviewer sees the raw error, clicks Unblock again and the whole set is
  // resent (the handler is idempotent per account). Advertiser-first would
  // clear the banner and the batch after the first chunk and leave the rest
  // unreachable from this page.
  const submitUnblock = async () => {
    const ids = [
      ...(unblockBatch ? batchMates.map(u => u.id) : []),
      advertiser.id,
    ];
    for (let i = 0; i < ids.length; i += MAX_BLOCK_TARGETS) {
      try {
        await unblockUsers({
          variables: { user_ids: ids.slice(i, i + MAX_BLOCK_TARGETS) },
        });
      } catch {
        return;
      }
    }
    setUnblockOpen(false);
  };

  return (
    <>
      {blocked ? (
        <Alert severity="error">
          <AlertTitle>{t('This advertiser is blocked')}</AlertTitle>
          {batchMates.length > 0
            ? t('Blocked on {{date}} together with {{count}} other account', {
                date: new Date(advertiser.fsbo_blocked_at!).toLocaleDateString(
                  locale,
                ),
                count: batchMates.length,
              })
            : t('Blocked on {{date}}', {
                date: new Date(advertiser.fsbo_blocked_at!).toLocaleDateString(
                  locale,
                ),
              })}
          {batchMates.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {batchMates
                .map(u => u.full_name?.trim() || t('Unnamed advertiser'))
                .join(', ')}
            </Typography>
          )}
          {advertiser.fsbo_blocked_reason != null &&
            advertiser.fsbo_blocked_reason.trim() !== '' && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('Internal note')}:
                </Box>{' '}
                <Box component="span" sx={{ fontStyle: 'italic' }}>
                  {advertiser.fsbo_blocked_reason}
                </Box>
              </Typography>
            )}
        </Alert>
      ) : accounts.length === 0 ? null : (
        <Alert
          severity={strong ? 'error' : 'warning'}
          icon={false}
          sx={{ '& .MuiAlert-message': { width: '100%' } }}
        >
          <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {t('{{count}} linked account', {
              count: accounts.length + hiddenAccounts,
            })}
            <Tooltip
              title={t(
                'Other FSBO advertisers of the last 90 days. Same browser is the same person. Same name or address is usually the same person registering again: compare the listings. A shared IP range alone can be innocent, customers of one provider share ranges, unless it belongs to a hosting or VPN company. Ticked accounts are blocked together with this advertiser.',
              )}
            >
              <Box
                component="span"
                sx={{ display: 'inline-flex', color: 'text.secondary' }}
              >
                <Info size={14} />
              </Box>
            </Tooltip>
          </AlertTitle>
          <Stack spacing={1}>
            {accounts.map(account => (
              <LinkedAccountRow
                key={account.userId}
                account={account}
                checked={selected.has(account.userId)}
                disabled={!canAct || busy}
                onToggle={toggle}
              />
            ))}
            {hiddenAccounts > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('and {{count}} more', { count: hiddenAccounts })}
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {canAct && (
        <Box sx={{ mt: blocked || accounts.length > 0 ? 1 : 0 }}>
          {blocked ? (
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => {
                setUnblockBatch(true);
                setUnblockOpen(true);
              }}
            >
              {t('Unblock advertiser')}
            </Button>
          ) : (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<Ban size={14} />}
              disabled={busy}
              onClick={openBlock}
            >
              {selectedAccounts.length > 0
                ? t('Block advertiser and {{count}} linked account', {
                    count: selectedAccounts.length,
                  })
                : t('Block advertiser')}
            </Button>
          )}
        </Box>
      )}

      <Dialog open={blockOpen} fullWidth maxWidth="sm" onClose={closeBlock}>
        <DialogTitle>
          {selectedAccounts.length > 0
            ? t('Block {{count}} accounts?', {
                count: selectedAccounts.length + 1,
              })
            : t('Block this advertiser?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t(
              'Their live FSBO listings are unpublished and put back in pending review, and the accounts are shadow-banned: every submission from them stays in pending review and never goes live. Nothing is sent to the advertisers.',
            )}
          </Typography>
          {selectable.length > 0 && (
            <Stack sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked disabled />}
                label={t('This advertiser')}
                slotProps={{ typography: { variant: 'body2' } }}
              />
              {selectable.map(account => (
                <FormControlLabel
                  key={account.userId}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.has(account.userId)}
                      onChange={e => toggle(account.userId, e.target.checked)}
                    />
                  }
                  label={
                    <>
                      {account.name ?? t('Unnamed advertiser')}{' '}
                      <Typography
                        variant="caption"
                        component="span"
                        color="text.secondary"
                      >
                        {account.kinds
                          .map(k => kindLabel(k, account.networkOwner, t))
                          .join(' · ')}
                      </Typography>
                    </>
                  }
                  slotProps={{ typography: { variant: 'body2' } }}
                />
              ))}
            </Stack>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t('Internal note (optional)')}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {overCap && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mr: 'auto', pl: 1 }}
            >
              {t(
                'At most {{max}} accounts can be blocked in one go — untick {{count}}',
                {
                  max: MAX_BLOCK_TARGETS,
                  count: 1 + selectedAccounts.length - MAX_BLOCK_TARGETS,
                },
              )}
            </Typography>
          )}
          <Button onClick={closeBlock}>{t('Cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || overCap}
            onClick={() => void submitBlock()}
          >
            {selectedAccounts.length > 0
              ? t('Block {{count}} accounts', {
                  count: selectedAccounts.length + 1,
                })
              : t('Block advertiser')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unblockOpen}
        fullWidth
        maxWidth="sm"
        onClose={() => setUnblockOpen(false)}
      >
        <DialogTitle>{t('Unblock this advertiser?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              'Their submissions go through the normal review again. Nothing is sent to the advertiser.',
            )}
          </Typography>
          {batchMates.length > 0 && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={unblockBatch}
                  onChange={e => setUnblockBatch(e.target.checked)}
                />
              }
              label={t(
                'Also unblock the {{count}} account blocked with this one',
                {
                  count: batchMates.length,
                },
              )}
              slotProps={{ typography: { variant: 'body2' } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblockOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => void submitUnblock()}
          >
            {t('Unblock advertiser')}
          </Button>
        </DialogActions>
      </Dialog>

      <Portal>
        <Snackbar
          open={lotFailures > 0}
          onClose={() => setLotFailures(0)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity="error" onClose={() => setLotFailures(0)}>
            {t(
              'Blocked, but {{count}} listing could not be put back in review and may still be live — set its status to Pending review by hand',
              { count: lotFailures },
            )}
          </Alert>
        </Snackbar>
      </Portal>

      <Portal>
        <Snackbar
          open={portalFailures > 0}
          onClose={() => setPortalFailures(0)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity="warning" onClose={() => setPortalFailures(0)}>
            {t(
              'Blocked, but {{count}} portal listing could not be unpublished — check the portals tab of their listings',
              { count: portalFailures },
            )}
          </Alert>
        </Snackbar>
      </Portal>

      <MutationErrorSnackbar
        error={blockState.error ?? unblockState.error ?? null}
        onClose={() => {
          blockState.reset();
          unblockState.reset();
        }}
      />
    </>
  );
};
